'use client';

import { useEffect, useMemo, useState } from 'react';
import type { Brand, Device } from '@/models';
import { listBrands, listDevices } from '@/services/catalog.service';
import { filterDevices, normalizeArabicText } from '@/lib/device-search';

export interface DeviceFinderApi {
  brands: Brand[];
  devices: Device[];
  filteredBrands: Brand[];
  visibleDevices: Device[];
  deviceCount: number;
  query: string;
  setQuery: (query: string) => void;
  selectedBrand: string | null;
  setSelectedBrand: (slug: string | null) => void;
  selectedBrandName: string;
  reset: () => void;
  isEmpty: boolean;
}

function filterBrands(brands: Brand[], search: string): Brand[] {
  const query = normalizeArabicText(search);
  if (!query) {
    return brands;
  }
  const tokens = query.split(' ').filter(Boolean);
  return brands.filter((brand) => {
    const haystack = normalizeArabicText(
      `${brand.nameAr} ${brand.name} ${brand.slug}`
    );
    return tokens.every((token) => haystack.includes(token));
  });
}

/**
 * Client-side state seam for the mobile finder browser.
 * Loads brands + devices once, then filters in memory (Arabic-tolerant,
 * no per-keystroke queries). Presentational views consume only this API.
 */
export function useDeviceFinder(): DeviceFinderApi {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [query, setQuery] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listBrands(), listDevices()])
      .then(([loadedBrands, loadedDevices]) => {
        if (!cancelled) {
          setBrands(loadedBrands);
          setDevices(loadedDevices.filter((device) => device.isActive !== false));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBrands([]);
          setDevices([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredBrands = useMemo(() => filterBrands(brands, query), [brands, query]);

  const selectedBrandId =
    selectedBrand != null
      ? (brands.find((brand) => brand.slug === selectedBrand)?.id ?? null)
      : null;

  const visibleDevices = useMemo(() => {
    const scoped =
      selectedBrand != null
        ? selectedBrandId != null
          ? devices.filter((device) => device.brandId === selectedBrandId)
          : []
        : devices;
    return filterDevices(scoped, query);
  }, [devices, selectedBrand, selectedBrandId, query]);

  const selectedBrandName =
    selectedBrand != null
      ? (brands.find((brand) => brand.slug === selectedBrand)?.nameAr ?? '')
      : '';

  const reset = () => {
    setQuery('');
    setSelectedBrand(null);
  };

  const showingModels = selectedBrand != null || query.trim() !== '';
  const isEmpty = showingModels
    ? visibleDevices.length === 0
    : filteredBrands.length === 0;

  return {
    brands,
    devices,
    filteredBrands,
    visibleDevices,
    deviceCount: visibleDevices.length,
    query,
    setQuery,
    selectedBrand,
    setSelectedBrand,
    selectedBrandName,
    reset,
    isEmpty,
  };
}
